import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Buildings, PencilSimple, CalendarCheck, CalendarX, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  active: "border-[var(--vs-success)]/40 text-[var(--vs-success)]",
  trial: "border-amber-500/50 text-amber-700",
  disabled: "border-red-500/50 text-red-700",
};

function statusBadge(row) {
  if (row.status === "disabled") return { label: "Disabled", cls: STATUS_COLORS.disabled };
  if (row.days_until_expiry !== null && row.days_until_expiry < 0) return { label: "Expired", cls: STATUS_COLORS.disabled };
  if (row.days_until_expiry !== null && row.days_until_expiry <= 30) return { label: `${row.days_until_expiry}d left`, cls: STATUS_COLORS.trial };
  if (row.status === "trial") return { label: "Trial", cls: STATUS_COLORS.trial };
  return { label: row.paid_until ? `Active · until ${row.paid_until}` : "Active", cls: STATUS_COLORS.active };
}

function EditDialog({ row, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    status: row.status || "active",
    plan_name: row.plan_name || "",
    billing_cycle_months: row.billing_cycle_months || "",
    paid_until: row.paid_until || "",
    last_payment_amount: "",
    notes: row.notes || "",
  });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        status: form.status,
        plan_name: form.plan_name || null,
        billing_cycle_months: form.billing_cycle_months ? Number(form.billing_cycle_months) : null,
        paid_until: form.paid_until || null,
        notes: form.notes || null,
      };
      if (form.last_payment_amount) body.last_payment_amount = Number(form.last_payment_amount);
      await api.put(`/admin/subscriptions/trusts/${row.trust_id}`, body);
      toast.success(`${row.trust_name} subscription updated.`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`edit-sub-${row.trust_slug}`}>
          <PencilSimple size={12} className="mr-1" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{row.trust_name}</DialogTitle>
          <DialogDescription>
            Configure plan, billing cycle, and paid-until date. Saving writes an audit log entry.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Status *</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="sub-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Plan name</Label>
              <Input
                placeholder="e.g. annual_premium"
                value={form.plan_name}
                onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                data-testid="sub-plan-name"
              />
            </div>
            <div>
              <Label>Billing cycle (months)</Label>
              <Input
                type="number" min="1" max="120"
                placeholder="12"
                value={form.billing_cycle_months}
                onChange={(e) => setForm({ ...form, billing_cycle_months: e.target.value })}
                data-testid="sub-cycle"
              />
            </div>
          </div>
          <div>
            <Label>Paid until (YYYY-MM-DD)</Label>
            <Input
              type="date"
              value={form.paid_until}
              onChange={(e) => setForm({ ...form, paid_until: e.target.value })}
              data-testid="sub-paid-until"
            />
          </div>
          <div>
            <Label>Last payment amount (INR, optional)</Label>
            <Input
              type="number" min="0" step="100"
              placeholder="Record this payment"
              value={form.last_payment_amount}
              onChange={(e) => setForm({ ...form, last_payment_amount: e.target.value })}
              data-testid="sub-last-payment"
            />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. paid by NEFT, invoice #..."
              data-testid="sub-notes"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[var(--vs-secondary)] text-[var(--vs-bg)]" data-testid="sub-save">
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TrustSubscriptionsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () =>
    api.get("/admin/subscriptions/trusts")
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const expired = rows.filter((r) => r.days_until_expiry !== null && r.days_until_expiry < 0).length;
    const warn = rows.filter((r) => r.days_until_expiry !== null && r.days_until_expiry >= 0 && r.days_until_expiry <= 30).length;
    const disabled = rows.filter((r) => r.status === "disabled").length;
    const active = rows.filter((r) => r.status === "active" && (r.days_until_expiry === null || r.days_until_expiry > 30)).length;
    return { expired, warn, disabled, active };
  }, [rows]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "expired") return rows.filter((r) => r.days_until_expiry !== null && r.days_until_expiry < 0);
    if (filter === "warn") return rows.filter((r) => r.days_until_expiry !== null && r.days_until_expiry >= 0 && r.days_until_expiry <= 30);
    if (filter === "disabled") return rows.filter((r) => r.status === "disabled");
    return rows;
  }, [rows, filter]);

  return (
    <DashboardLayout title="Trust Subscriptions" subtitle="Configure per-trust plans, expiry, and billing cycles. Super-admin only.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { k: "active", label: "Active", color: "text-[var(--vs-success)]", icon: CalendarCheck },
          { k: "warn", label: `≤30 days`, color: "text-amber-700", icon: CalendarX },
          { k: "expired", label: "Expired", color: "text-red-700", icon: CalendarX },
          { k: "disabled", label: "Disabled", color: "text-red-700", icon: Receipt },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.k}
              onClick={() => setFilter(filter === s.k ? "all" : s.k)}
              className={`vs-card p-4 text-left transition-colors hover:bg-[var(--vs-bg-subtle)] ${filter === s.k ? "ring-2 ring-[var(--vs-secondary)]" : ""}`}
              data-testid={`sub-kpi-${s.k}`}
            >
              <div className="flex items-center justify-between">
                <Icon size={18} className={s.color} weight="bold" />
                <div className={`text-2xl font-display font-semibold ${s.color}`}>{stats[s.k]}</div>
              </div>
              <div className="text-xs mt-2 uppercase tracking-widest text-[var(--vs-text-secondary)]">{s.label}</div>
            </button>
          );
        })}
      </div>

      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">Trust</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Cycle</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last payment</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No trusts match this filter.</td></tr>
            )}
            {filtered.map((row) => {
              const b = statusBadge(row);
              return (
                <tr key={row.trust_id || `idx-${row.trust_slug || row.trust_name}`} className="border-t border-[var(--vs-border)]" data-testid={`sub-row-${row.trust_slug || row.trust_id}`}>
                  <td className="px-4 py-3 font-medium flex items-center gap-2">
                    <Buildings size={14} /> {row.trust_name}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{row.plan_name || "—"}</td>
                  <td className="px-4 py-3 text-xs">{row.billing_cycle_months ? `${row.billing_cycle_months}m` : "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={b.cls}>{b.label}</Badge></td>
                  <td className="px-4 py-3 text-xs">
                    {row.last_payment_amount ? `₹${Number(row.last_payment_amount).toLocaleString("en-IN")}` : "—"}
                    {row.last_payment_at && <div className="text-[10px] text-[var(--vs-text-secondary)]">{row.last_payment_at.slice(0, 10)}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <EditDialog row={row} onSaved={load} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
